import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ZoneCreateRequest, ZoneResponse } from '@key-telematics/fleet-api-client';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from 'app/app.service';
import { AssetGroupingService, MeasurementUnitsService, MetricsOptionsService, MetricsService } from 'app/services';
import { FormBuilderDefinition, IdNameComboValueHandlers } from 'app/shared/components/form-builder';
import { Subject } from 'rxjs';
import { MapZonesService, ZoneProperties } from '../../map-zones.service';
import { Point } from '../../map.component';
import { ZoneEditorAction, ZoneType } from '../model';

export interface ZoneEditEvent {
    action: ZoneEditorAction;
    zoneId?: string;
    doubleClicked?: boolean;
}

export interface ZoneForm {
    form: FormBuilderDefinition
    values: ZoneProperties,
}

enum ZoneEditAction {
    NONE,
    GEOFENCE_CREATE,
    GEOFENCE_EDIT,
    ROUTE_CREATE,
    ROUTE_EDIT,
}

@Injectable()
export class ZoneEditorService {
    private static readonly ZONE_CREATION_ACTIONS: ZoneEditorAction[] = ['zoneaddpolygon', 'zoneaddpolyline']

    private _zoneAutoCompleteGeometry = new Subject<boolean>();
    private _zoneCompleteEditSubject = new Subject<boolean>();
    private _zoneEditCancelSubject = new Subject<boolean>();
    private _zoneEditUndoSubject = new Subject<boolean>();
    private _zoneEditEventSubject = new Subject<ZoneEditEvent>();

    zoneCreated$ = this.zoneService.zoneCreated$;
    polygonEdit$ = this.zoneService.polygonEdit$;
    zoneEditEvent$ = this._zoneEditEventSubject.asObservable();
    autoCompleteGeometry$ = this._zoneAutoCompleteGeometry.asObservable();
    zoneCompleteEdit$ = this._zoneCompleteEditSubject.asObservable();
    zoneEditCancel$ = this._zoneEditCancelSubject.asObservable();
    zoneEditUndo$ = this._zoneEditUndoSubject.asObservable();

    //Analytics variables
    private metricsOptions = inject(MetricsOptionsService);
    private metrics = inject(MetricsService);

    private  zoneEditAction:  WritableSignal<ZoneEditAction> = signal(ZoneEditAction.NONE);
    //private  analyticsSource: WritableSignal<string> = signal('');
    private  isGeofence: WritableSignal<boolean> = signal(false);

    private readonly event = computed(() => {
        switch(this.zoneEditAction()) {
            case ZoneEditAction.GEOFENCE_CREATE:
                return 'geofence_create';
            case ZoneEditAction.GEOFENCE_EDIT:
                return 'geofence_edit';
            case ZoneEditAction.ROUTE_EDIT:
                return 'route_edit';
            case ZoneEditAction.ROUTE_CREATE:
                return 'route_create';
            default:
                return '';
        }
    }) 

    constructor(
        protected app: AppService,
        protected i18n: TranslateService,
        private grouping: AssetGroupingService,
        private units: MeasurementUnitsService,
        private zoneService: MapZonesService,
    ) {}

    fireEditEvent(editEvent: ZoneEditEvent) {
        this._zoneEditEventSubject.next(editEvent);

        switch (editEvent.action) {
            case 'zoneaddpolygon':
            case 'zoneaddpolyline':
                this.zoneEditAction.set(this.isGeofence() ? ZoneEditAction.GEOFENCE_CREATE : ZoneEditAction.ROUTE_CREATE);
                this.captureAnalytics('start');
                break;
            case 'zoneedit':
                    this.zoneEditAction.set(this.isGeofence() ? ZoneEditAction.GEOFENCE_EDIT : ZoneEditAction.ROUTE_EDIT);
                    //Becuase you can double click a geofence/route on the map, we need to fire the start in this instance as well as the select.
                    if (editEvent.doubleClicked) {
                        this.captureAnalytics('start');
                    }
                    this.captureAnalytics(this.isGeofence() ? 'select_geofence' : 'select_route');
                break;
        }
    }

    autoCompleteGeometry() {
        this._zoneAutoCompleteGeometry.next(true);
        this.captureAnalytics('confirm_drawing');
    }

    /**
     * Completes map edits
     * @param deleted if the map object has been deleted.
     * @param ignoreAnalyticsCapture this is used if analytics has been recorded elsewhere and use this funtion and we want to ingore the analytics here.
     */
    completeMapEdits(deleted: boolean = false, ignoreAnalyticsCapture: boolean = false) {
        this._zoneCompleteEditSubject.next(true);

        if (!ignoreAnalyticsCapture) {
            if (deleted) {
                this.captureAnalytics('delete');
            } else {
                this.captureAnalytics('confirm');
            }
        }
    }

    cancelMapEditing() {
        this._zoneEditCancelSubject.next(true);
    }

    removeLastPoint() {
        this._zoneEditUndoSubject.next(true);
        this.captureAnalytics('delete_point')
    }

    initialZonePointAdded() {
        this.captureAnalytics('draw');
    }

    manageGeofencesOrRoutes() {
        this.zoneEditAction.set(this.isGeofence() ? ZoneEditAction.GEOFENCE_EDIT : ZoneEditAction.ROUTE_EDIT);
        this.captureAnalytics('start');
    }

    async getForm(zoneAction: ZoneEditorAction, zoneId?: string): Promise<ZoneForm> {
        // check if we're editing a zone or creating a new one
        if (!ZoneEditorService.ZONE_CREATION_ACTIONS.includes(zoneAction)) {
            // we are editing an existing zone
            const zone = await this.app.api.entities.getZone(zoneId);
            const values = {
                name: zone.name,
                zoneType: zone.zoneType,
                costCentre: zone.costCentre,
                group: zone.group,
                speed: zone.speed && this.units.fromBackend('speed', zone.speed, 0),
                radius: zone.radius && this.units.fromBackend('distance', zone.radius, 3),
            };
            const form = await this.getFormForType(zone.zoneType);
            return {
                values,
                form
            };
        } else {
            // we are creating a new zone
            const newZoneType = zoneAction === 'zoneaddpolygon' ? 'location' : 'route';
            const values: ZoneProperties = {
                zoneType: newZoneType
            };
            const form = await this.getFormForType(newZoneType);
            return {
                values,
                form
            };
        }
    }

    updateZone(zoneId: string, update: ZoneProperties): Promise<ZoneResponse> {
        return this.zoneService.updateZone(zoneId, update);
    }

    createNewZone(zoneName: string, points: Point[], propeties: ZoneProperties) {
        const request: ZoneCreateRequest = {
            ...propeties,
            ownerId: this.app.client.id,
            name: zoneName,
            points
        }   
        return this.zoneService.createZone(request);
    }

    private async getFormForType(zoneType: ZoneType): Promise<FormBuilderDefinition> {
        const [zoneGroups, costCentres] = await Promise.all([
            this.grouping.getZoneGroupsAsTree(),
            this.grouping.getCostCentresAsTree(),
        ]);
        const zoneTypes = ['location', 'keepin', 'nogo'].map(key => ({ key, value: this.i18n.instant(`SHARED.ZONE_TYPES.${key.toUpperCase()}`) }));
        const translate = (key: string) => this.i18n.instant(`ADMIN.EDITORS.ZONE.DETAILS.FIELDS.${key}`);
        const form = {
            groups: [{
                fields: [
                    {
                        id: 'name',
                        title: translate('NAME'),
                        type: 'text',
                        required: true,
                        min: 1,
                        max: 255,
                    },
                    zoneType === 'route' ? null : {
                        id: 'zoneType',
                        title: translate('TYPE'),
                        type: 'combo',
                        required: true,
                        values: zoneTypes
                    },
                    {
                        id: 'costCentre',
                        title: translate('COST_CENTRE'),
                        type: 'combo',
                        required: true,
                        values: AssetGroupingService.treeToComboValues(costCentres),
                        ...IdNameComboValueHandlers
                    },
                    {
                        id: 'group',
                        title: translate('GROUP'),
                        type: 'combo',
                        required: true,
                        values: AssetGroupingService.treeToComboValues(zoneGroups),
                        ...IdNameComboValueHandlers
                    },
                    zoneType === 'route' ? null : {
                        id: 'speed',
                        title: translate('SPEED'),
                        type: 'number',
                        required: false,
                        min: 0,
                        max: 1000,
                        unit: this.units.format(0, 'speed').unit,
                    },
                    zoneType === 'route' ? null : {
                        id: 'radius',
                        title: translate('RADIUS'),
                        type: 'number',
                        required: false,
                        min: 0,
                        max: 1000,
                        unit: this.units.format(0, 'distance').unit,
                    },
                ].filter(x => x),
            }],
        };
        return form;
    }

    //#region Analytics.


    //#region Public Methods 

    public setIsGeofence(source: boolean) {
        this.isGeofence.set(source);
    }

    //#endregion

    //#region  Private Methods

    private captureAnalytics(step: 'start' | 'draw' | 'delete_point' | 'confirm_drawing' | 'confirm' | 'select_geofence' | 'delete' | 'select_route') {
        const isLiveMap = this.metricsOptions.isPage('live_map');
        const isTripHistory = this.metricsOptions.isPage('trip_history');

        if (!isLiveMap && !isTripHistory) return;

        this.metrics.capture(this.event(), {
            screen: this.metricsOptions.getScreenFromUrl(),
            source: this.metricsOptions.source(),
            step: step
        });
    }

    //#endregion

    //#endregion
}
